/**
 * Finanzen — Hub mit Reitern, Vorbild ist die Desktop-App
 * (apps/vimaana-desktop/src/pages/FinanzenHubPage.tsx).
 *
 * Fuenf Reiter: Belege (Register, Postfach-Abruf, Upload), Konto (Salden,
 * Bankbewegungen), Inhaber (Gesellschafterkonten), Bilanz (Umsatz gegen
 * Kosten) und zuletzt der Ausgangskorb (was noch zu ueberweisen ist). Lieferscheine und
 * Autopilot fehlen gegenueber dem Desktop noch.
 *
 * Der gewaehlte Reiter steht in der Adresse (?reiter=…), damit ein Neuladen und
 * ein geteilter Link dieselbe Ansicht zeigen.
 */

import { lazy, Suspense } from "react";
import type { ReactElement } from "react";
import { useSearchParams } from "react-router-dom";
import "../styles/belege.css";
import "../styles/auswertung.css";
import "../styles/zahllauf.css";
import "../styles/datev.css";

const BelegRegister = lazy(() => import("../components/belege/BelegRegister"));
const ZahllaufTab = lazy(() => import("../components/finanzen/ZahllaufTab"));
const KontoTab = lazy(() => import("../components/finanzen/KontoTab"));
const InhaberTab = lazy(() => import("../components/finanzen/InhaberTab"));
const BilanzTab = lazy(() => import("../components/finanzen/BilanzTab"));

type Reiter = "belege" | "zahllauf" | "konto" | "inhaber" | "bilanz";

const REITER: { id: Reiter; label: string }[] = [
  { id: "belege", label: "Belege" },
  { id: "konto", label: "Konto" },
  { id: "inhaber", label: "Inhaber" },
  { id: "bilanz", label: "Bilanz" },
  // Der Ausgangskorb steht am Ende: er ist der letzte Schritt der Kette und
  // keine Ansicht, die man beim Durchgehen der Finanzen zuerst braucht.
  { id: "zahllauf", label: "Ausgangskorb" },
];

export default function FinanzenPage(): ReactElement {
  const [params, setParams] = useSearchParams();
  const roh = params.get("reiter");
  const aktiv: Reiter =
    REITER.some((r) => r.id === roh) ? (roh as Reiter) : "belege";

  const waehlen = (r: Reiter) => {
    params.set("reiter", r);
    setParams(params, { replace: true });
  };

  return (
    <div className="fin-hub">
      <h1 style={{ margin: 0, fontSize: 26, fontWeight: 800 }}>Finanzen</h1>
      <div style={{ color: "var(--vg-text-2)", margin: "4px 0 16px", fontSize: 13 }}>
        Belege, offene Ueberweisungen, Kontostaende, Gesellschafterkonten und Ergebnis
      </div>

      {/* Rechts neben den Reitern die Ecke für die Werkzeuge des aktiven
          Reiters (Belege: holen, nachziehen, Datei) — BelegWerkzeuge. */}
      <div className="fin-hub-kopf">
        <div role="tablist" aria-label="Finanzen" className="fin-hub-tabs">
          {REITER.map((r) => (
            <button
              key={r.id}
              type="button"
              role="tab"
              aria-selected={aktiv === r.id}
              className={aktiv === r.id ? "fin-hub-tab is-active" : "fin-hub-tab"}
              onClick={() => waehlen(r.id)}
            >
              {r.label}
            </button>
          ))}
        </div>
        <div id="fin-hub-ecke" className="fin-hub-ecke" />
      </div>

      <Suspense fallback={<div style={{ padding: "2rem", color: "var(--vg-text-2)" }}>Lade …</div>}>
        {aktiv === "belege" && <BelegRegister />}
        {aktiv === "zahllauf" && <ZahllaufTab />}
        {aktiv === "konto" && <KontoTab />}
        {aktiv === "inhaber" && <InhaberTab />}
        {aktiv === "bilanz" && <BilanzTab />}
      </Suspense>
    </div>
  );
}
